import React, { useEffect, useState } from 'react';
import Modal from '../../../components/admin/Modal';
import { api } from '../../../lib/api';
import AccountAvatar from '../../../components/crm/AccountAvatar';

export default function ReconcileProspectModal({ open, account, onClose, onReconciled }) {
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [searchedPseudoDbns, setSearchedPseudoDbns] = useState([]);
  const [kind, setKind] = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/api/admin/accounts/${account.id}/reconcile-candidates`);
        if (cancel) return;
        setCandidates(res.candidates || []);
        setSearchedPseudoDbns(res.searchedPseudoDbns || []);
        setKind(res.kind);
      } catch (err) {
        if (!cancel) setError(err.message || 'Failed to load candidates');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, account.id]);

  const confirm = async () => {
    if (!selected) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post(`/api/admin/accounts/${account.id}/reconcile-prospect`, {
        prospect_account_id: selected.account.id,
      });
      onReconciled?.(res.account);
    } catch (err) {
      if (err.code === 'prospect_has_linked_data') {
        const c = err.data?.counts || {};
        setError(`This prospect has linked data: ${Object.entries(c).map(([k, v]) => `${v} ${k}`).join(', ')}. Manual reconciliation required.`);
      } else {
        setError(err.message || 'Reconcile failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Reconcile with seeded prospect" maxWidth="max-w-xl">
      <div className="space-y-4">
        <div className="text-[12px] text-gray-400">
          Existing account <span className="text-white">{account.name}</span> has no DBN set. Candidates below match on borough + school number.
        </div>

        {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-2">{error}</div>}

        {loading ? (
          <div className="animate-pulse h-16 rounded-lg bg-white/[0.03]" />
        ) : candidates.length === 0 ? (
          <div className="text-[13px] text-gray-400 p-4 rounded-lg border border-white/[0.05] bg-white/[0.015]">
            No matching prospect found for <span className="text-white">{account.name}</span>.
            {kind !== 'resolvable' && <> The name couldn't be parsed into a P.S./M.S./I.S. + number pattern ({kind}).</>}
            <div className="mt-2 text-[11px] text-gray-500">Use the admin UI to manually set DBN, or edit the account name to match a DOE pattern and try again.</div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-[11px] text-gray-500">Searched pseudo-DBNs: {searchedPseudoDbns.join(', ')}</div>
            {candidates.map((c) => (
              <button
                key={c.account.id}
                onClick={() => setSelected(c)}
                className={`w-full flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors text-left ${
                  selected?.account?.id === c.account.id
                    ? 'border-[#B0FC38]/40 bg-[#B0FC38]/10'
                    : 'border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.03]'
                }`}
              >
                <AccountAvatar account={c.account} showStage={false} />
                <div className="shrink-0 text-right">
                  <div className="text-[11px] font-mono text-gray-400">{c.account.typeSchoolDbn}</div>
                  <div className="text-[10px] text-gray-600">D{c.account.typeSchoolDistrict} · {c.account.typeSchoolBorough}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {selected && (
          <div className="p-3 rounded-lg border border-[#B0FC38]/25 bg-[#B0FC38]/5 text-[12px] text-gray-300">
            <div className="font-medium text-white mb-1">Confirm reconcile</div>
            <div>This will copy DBN / district / borough / address / principal / superintendent from the prospect onto <span className="text-white">{account.name}</span>, then delete the prospect row. Existing fields on this account are preserved unless NULL.</div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-[13px] text-gray-400 hover:text-white">Cancel</button>
          <button
            onClick={confirm}
            disabled={!selected || submitting}
            className="px-4 py-1.5 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Reconciling…' : 'Confirm reconcile'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
